import { useState, useRef } from "react";
import { Upload, RefreshCw, FileDown, Shield, FileText, FileSpreadsheet, Code2, FileJson } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";

export default function Admin() {
    const { t } = useApp();
    const [file, setFile] = useState(null);
    const [replace, setReplace] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    const onUpload = async () => {
        if (!file) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await api.uploadPdf(file, replace);
            setResult(r);
            toast.success(`Uploaded: ${r.inserted} inserted, ${r.updated} updated`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const onReseed = async () => {
        setBusy(true);
        setResult(null);
        try {
            const r = await api.reseed();
            setResult(r);
            toast.success(`Reseeded: ${r.merged} entries`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Re-parse failed");
        } finally {
            setBusy(false);
        }
    };

    const ExportLink = ({ href, icon: Icon, label, tid }) => (
        <a
            href={href}
            data-testid={tid}
            className="rounded-3xl bg-card border border-border/70 p-5 flex items-center gap-3 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all"
        >
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center text-white">
                <Icon className="h-5 w-5" />
            </div>
            <div>
                <div className="font-display font-bold">{label}</div>
                <div className="text-xs text-muted-foreground">Download all LO codes</div>
            </div>
        </a>
    );

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-start gap-3">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center text-white shadow-md">
                    <Shield className="h-6 w-6" />
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{t.admin}</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t.adminTitle}</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t.adminDesc}</p>
                </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
                {/* Upload */}
                <div className="rounded-3xl bg-card border border-border/70 p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <Upload className="h-5 w-5 text-primary" />
                        <h2 className="font-display font-bold text-lg">{t.uploadPdf}</h2>
                    </div>
                    <div
                        onClick={() => inputRef.current?.click()}
                        className="border-2 border-dashed border-border rounded-2xl p-6 text-center cursor-pointer hover:bg-muted/40 transition-colors"
                    >
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <div className="font-semibold text-sm">{file ? file.name : "Click to choose PDF"}</div>
                        <div className="text-xs text-muted-foreground mt-1">.pdf only</div>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="admin-file-input"
                        />
                    </div>
                    <label className="flex items-center gap-2 mt-4 text-sm">
                        <input
                            type="checkbox"
                            data-testid={TIDS.adminReplaceToggle}
                            checked={replace}
                            onChange={(e) => setReplace(e.target.checked)}
                            className="h-4 w-4 rounded"
                        />
                        <span>{t.replaceAll}</span>
                    </label>
                    <button
                        onClick={onUpload}
                        disabled={!file || busy}
                        data-testid={TIDS.adminUpload}
                        className="w-full mt-4 rounded-full bg-primary text-primary-foreground py-3 font-semibold text-sm active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                    >
                        <Upload className="h-4 w-4" />
                        {busy ? t.loading : t.uploadPdf}
                    </button>
                </div>

                {/* Reseed */}
                <div className="rounded-3xl bg-card border border-border/70 p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <RefreshCw className="h-5 w-5 text-accent" />
                        <h2 className="font-display font-bold text-lg">{t.reparse}</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        Re-download and re-parse the original Odisha LO Book PDF from its source URL.
                    </p>
                    <button
                        onClick={onReseed}
                        disabled={busy}
                        data-testid={TIDS.adminReseed}
                        className="w-full rounded-full bg-accent text-accent-foreground py-3 font-semibold text-sm active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
                        {busy ? t.loading : t.reparse}
                    </button>

                    {result && (
                        <div className="mt-4 rounded-2xl bg-muted p-3 text-xs font-mono">
                            <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
                        </div>
                    )}
                </div>
            </div>

            <h2 className="font-display font-bold text-xl mb-3 flex items-center gap-2">
                <FileDown className="h-5 w-5" /> {t.export}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <ExportLink href={api.exportUrl("pdf")} icon={FileText} label="PDF" tid={TIDS.exportPdf} />
                <ExportLink href={api.exportUrl("excel")} icon={FileSpreadsheet} label="Excel" tid={TIDS.exportExcel} />
                <ExportLink href={api.exportUrl("csv")} icon={Code2} label="CSV" tid={TIDS.exportCsv} />
                <ExportLink href={api.exportUrl("json")} icon={FileJson} label="JSON" tid={TIDS.exportJson} />
            </div>
        </div>
    );
}
